import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Stack, useMediaQuery } from '@mui/material'
import { H1, Body } from 'components/styledFonts'
import Block from 'components/Block'
import { useFormContext } from 'react-hook-form'
import {
    CharacterLimitField,
    DatePicker,
    ImageCaption,
    LabeledInputField,
    RadioGroupBase,
    Required,
    TextField,
} from 'components/Inputs'
import { SelectMovie } from 'components/FileUploader'
import { maxLength } from './NewsLetterSubmissionBase'
import WysiwygEditor from 'components/WysiwygEditor'
import InquiryUserInput from 'components/InquiryUserInput'
import FeedbackCheckbox from 'components/FeedbackCheckbox'
import CategorySelect from 'components/CategorySelect'
import GenreSelect from 'components/GenreSelect'
import PostButtonSet from 'components/PostButtonSet'
import { useNewsLetterByNo } from 'hooks/newsletter'

const NewsLetterHtmlSubmission = () => {
    const { control, setValue, handleSubmit, watch, saveDraft } = useFormContext()
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('mobile'))
    const { no } = useParams()
    const { isFetching } = useNewsLetterByNo(no)

    // 入稿方法の上書き
    useEffect(() => {
        setValue("入稿方法", "HTML入稿")
    }, [setValue])

    // Trumbowygで入力した内容をsubmit直前にformに反映
    const preSubmit = e => {
        const $this = window.$(`#本文`)
        if ($this.parent().find('.trumbowyg-viewHTML-button').hasClass('trumbowyg-active')) {
            const html = $this.prev().val()
            setValue(`本文`, html || '')
            $this.empty().html(html)
        }
        else {
            setValue(`本文`, $this.html() || '')
        }
    }


    return (
        <Stack
            gap={2}
        >
            <H1>{`ニュースレター${no ? "の下書きを編集" : "を投稿"}`}</H1>
            <Block>
                <Body sx={{ pb : 2 }}><Required />がついている項目は必須です</Body>
                <Stack
                    component="form"
                    gap={4}
                    pb={2}
                    sx={{
                        pl : { mobile : 2, xs : 0 },
                        pr : { mobile : 8, xs : 0 },
                    }}
                >
                    <CharacterLimitField
                        label="タイトル"
                        name="ネタたねタイトル"
                        limit={maxLength.タイトル}
                        control={control}
                        required
                        multiline
                        disableEnter
                        watch={watch('ネタたねタイトル')}
                    />
                    <CharacterLimitField
                        name="リード文"
                        limit={maxLength.リード文}
                        control={control}
                        required
                        multiline
                        minRows={3}
                        watch={watch("リード文")}
                    />
                    <LabeledInputField
                        label="メイン画像"
                        note="※一覧ページでサムネイルとしても表示されます。画像は最大5MBまでアップロード可能です。"
                        required
                    >
                        <ImageCaption
                            name="メインイメージ"
                            captionName="メインイメージキャプション"
                            pict={watch('メインイメージ')}
                            control={control}
                            setValue={setValue}
                        />
                    </LabeledInputField>
                    <LabeledInputField
                        label={"本文"}
                        note="※iframeタグは使用出来ません。"
                        required
                    >
                        <WysiwygEditor
                            name={"本文"}
                            control={control}
                            initValue={watch('本文')}
                            limit={maxLength.本文}
                        />
                    </LabeledInputField>
                    <LabeledInputField
                        label="画像"
                        note="※最大4つまで掲載可、画像は最大5MBまでアップロード可能です。"
                    >
                    {[...Array(4).keys()].map((k, i, a) => {
                    return (
                        <ImageCaption
                            key={i}
                            name={`イメージ${i + 1}`}
                            captionName={`イメージ${i + 1}キャプション`}
                            label="画像"
                            pict={watch(`イメージ${i + 1}`)}
                            control={control}
                            setValue={setValue}
                        />
                    )})}
                    </LabeledInputField>
                    <LabeledInputField
                        label="動画"
                        note="※動画は最大50MBまでアップロード可能です。"
                    >
                        <RadioGroupBase
                            value={watch(`アップロード方法`)}
                            onChange={e => setValue(`アップロード方法`, e.target.value)}
                            row={!isMobile}
                            sx={{
                                columnGap : 0,
                            }}
                            list={[
                                { label : '動画をアップロード', value : '動画をアップロード', disabled : !!watch(`動画URL`) },
                                { label : '動画の埋め込みタグを入力', value : '動画の埋め込みタグを入力', disabled : !!(watch(`動画`)?.url || watch(`動画`)?.blob) }
                            ]}
                        />
                        {watch(`アップロード方法`) === "動画をアップロード" &&
                        <SelectMovie
                            name={`動画`}
                            file={watch(`動画`)}
                            control={control}
                            setValue={setValue}
                            showName
                            removable
                        />
                        }
                        {watch(`アップロード方法`) === "動画の埋め込みタグを入力" && <>
                        <TextField
                            name={`動画URL`}
                            control={control}
                            watch={watch(`動画URL`)}
                            placeholder="YouTube動画の埋め込みタグを貼り付けてください。"
                        />
                        <Body>{"YouTube動画の「共有メニュー > 埋め込む」から取得できる、埋め込み用タグをコピーアンドペーストしてください。"}</Body>
                        </>}
                    </LabeledInputField>
                    <GenreSelect control={control} />
                    <CategorySelect control={control} />
                    <InquiryUserInput
                        control={control}
                        setValue={setValue}
                        note="企業情報設定と異なる方を問い合わせ担当者にする際は入力してください"
                    />
                    <LabeledInputField
                        label="投稿日時"
                    >
                        <DatePicker
                            name="公開日"
                            control={control}
                            showTimeInput
                        />
                    </LabeledInputField>
                    <FeedbackCheckbox
                        name={"フィードバック依頼"}
                        disabled={!!watch("フィードバックNo")}
                        control={control}
                    />
                </Stack>
            </Block>
            <PostButtonSet
                handleSubmit={handleSubmit}
                watch={watch}
                saveDraft={saveDraft}
                isFeedback={watch("フィードバック依頼") && !watch("フィードバックNo")}
                preSubmit={preSubmit}
                isFetching={isFetching}
            />
        </Stack>
    )
}

export default NewsLetterHtmlSubmission
